import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { PREMISSIONS } from 'src/app/core/enums/permissions';
import { OnboardingExperienceLevel, OnboardingRole, OnboardingService } from './onboarding.service';
import { UserExperienceLevelService } from './user-experience-level.service';
import {
  MandatoryOnboardingStatus,
  MandatoryOnboardingStep,
  MandatoryStepProgress,
  OnboardingJourneyState,
} from '../models/onboarding-journey.model';
import type { JourneyProgressSnapshot } from '../models/journey-progress.model';

/** صاحب منشأة مبتدئ: عملاء/أصناف ثم بقية الخطوات */
const FLOW_STEPS_OWNER_BEGINNER: MandatoryOnboardingStep[] = [
  'company',
  'currency',
  'seedData',
  'store',
  'productGroup',
  'barcode',
  'zatcaUnit',
  'electronicInvoicing',
  'done',
];

/** صاحب منشأة متوسط: بدون مرحلة العملاء/الأصناف التفاعلية */
const FLOW_STEPS_OWNER_INTERMEDIATE: MandatoryOnboardingStep[] = [
  'company',
  'currency',
  'store',
  'productGroup',
  'barcode',
  'zatcaUnit',
  'electronicInvoicing',
  'done',
];

/** صاحب منشأة خبير: شركة وعملات فقط ثم لوحة التحكم */
const FLOW_STEPS_OWNER_EXPERT: MandatoryOnboardingStep[] = ['company', 'currency', 'done'];

/** محاسب مبتدئ: شركة → عملة → عام مالي → دليل حسابات → سند قبض → سند صرف → جرد مخزون → مخزون افتتاحي */
const FLOW_STEPS_ACCOUNTANT_BEGINNER: MandatoryOnboardingStep[] = [
  'company',
  'currency',
  'fiscalYear',
  'glAccounts',
  'receiptVoucher',
  'paymentVoucher',
  'manualInventory',
  'openingStock',
  'done',
];

/** محاسب متوسط: شركة → عملة → عام مالي → دليل حسابات → جرد مخزون → مخزون افتتاحي */
const FLOW_STEPS_ACCOUNTANT_INTERMEDIATE: MandatoryOnboardingStep[] = [
  'company',
  'currency',
  'fiscalYear',
  'glAccounts',
  'manualInventory',
  'openingStock',
  'done',
];

/** محاسب خبير: شركة → عملة → عام مالي → حسابات وسيطة */
const FLOW_STEPS_ACCOUNTANT_EXPERT: MandatoryOnboardingStep[] = [
  'company',
  'currency',
  'fiscalYear',
  'intermediateAccount',
  'done',
];

/** تقني مبتدئ: مستخدمين → أدوار → فروع */
const FLOW_STEPS_TECHNICIAN_BEGINNER: MandatoryOnboardingStep[] = [
  'users',
  'roles',
  'branch',
  'zatcaUnit',
  'electronicInvoicing',
  'done',
];

/** تقني متوسط: مستخدمين → أدوار */
const FLOW_STEPS_TECHNICIAN_INTERMEDIATE: MandatoryOnboardingStep[] = [
  'users',
  'roles',
  'zatcaUnit',
  'electronicInvoicing',
  'done',
];

/** تقني خبير: أدوار فقط */
const FLOW_STEPS_TECHNICIAN_EXPERT: MandatoryOnboardingStep[] = ['roles', 'done'];

const ENABLE_BACKEND_SYNC = false;

/** Must match `ContactWizardService` key: `contact_wizard_progress_` + `CUSTOMERS`. */
const CONTACT_WIZARD_PROGRESS_CUSTOMERS = 'contact_wizard_progress_CUSTOMERS';

/** قواعد الصلاحية للخطوة: `all` = يجب توفر كل المفاتيح (مثل عميل + صنف في seedData). */
interface MandatoryPermissionRule {
  readonly keys: readonly string[];
  readonly mode: 'any' | 'all';
}

const MANDATORY_STEP_REQUIRED_PERMISSIONS: Partial<Record<MandatoryOnboardingStep, MandatoryPermissionRule>> = {
  /** Company edit often uses Company.* claims; GeneralSettings may be absent. */
  company: {
    keys: [
      PREMISSIONS.GeneralSettings.View,
      PREMISSIONS.GeneralSettings.UpSert,
      PREMISSIONS.Company.View,
      PREMISSIONS.Company.Update,
    ],
    mode: 'any',
  },
  currency: { keys: [PREMISSIONS.Currencies.Create], mode: 'any' },
  fiscalYear: { keys: [PREMISSIONS.FinancialYear.View], mode: 'any' },
  glAccounts: { keys: [PREMISSIONS.GLAccount.View], mode: 'any' },
  intermediateAccount: { keys: [PREMISSIONS.IntermediateAccount.View], mode: 'any' },
  receiptVoucher: { keys: [PREMISSIONS.ReceiptVoucher.View], mode: 'any' },
  paymentVoucher: { keys: [PREMISSIONS.PaymentVoucher.View], mode: 'any' },
  seedData: {
    keys: [PREMISSIONS.Customer.Create, PREMISSIONS.Products.Create],
    mode: 'all',
  },
  store: { keys: [PREMISSIONS.Store.Create], mode: 'any' },
  productGroup: { keys: [PREMISSIONS.ProductGroup.Create], mode: 'any' },
  barcode: { keys: [PREMISSIONS.Products.View], mode: 'any' },
  users: { keys: [PREMISSIONS.Users.Create], mode: 'any' },
  roles: { keys: [PREMISSIONS.Roles.Create], mode: 'any' },
  branch: { keys: [PREMISSIONS.Branch.Create], mode: 'any' },
  dailyJournal: { keys: [PREMISSIONS.Journal.View], mode: 'any' },
  salesInvoice: { keys: [PREMISSIONS.Document.Sales.Create], mode: 'any' },
  purchaseInvoice: { keys: [PREMISSIONS.Document.Purches.Create], mode: 'any' },
  traineeCustomer: { keys: [PREMISSIONS.Customer.View], mode: 'any' },
  traineeVendor: { keys: [PREMISSIONS.Vendor.View], mode: 'any' },
  traineeProduct: { keys: [PREMISSIONS.Products.View], mode: 'any' },
  shift: { keys: [PREMISSIONS.Shift.View], mode: 'any' },
  pos: { keys: [PREMISSIONS.Document.Sales.Create], mode: 'any' },
  manualInventory: { keys: [PREMISSIONS.ManualInventory.View], mode: 'any' },
  openingStock: { keys: [PREMISSIONS.OpeningStock.View], mode: 'any' },
  zatcaUnit: { keys: [PREMISSIONS.ZatcaUnits.Create], mode: 'any' },
  electronicInvoicing: { keys: [PREMISSIONS.Document.ZATCA.View], mode: 'any' },
};

type RemoteProfilePayload = {
  isOnboardingCompleted?: boolean;
  onboardingRole?: OnboardingRole;
  onboardingExperienceLevel?: OnboardingExperienceLevel;
  onboardingCurrentStep?: MandatoryOnboardingStep;
  onboardingStatus?: MandatoryOnboardingStatus;
  postOnboardingModalShown?: boolean;
  onboardingJourney?: Partial<{
    status: MandatoryOnboardingStatus;
    currentStep: MandatoryOnboardingStep;
    postModalShown: boolean;
  }>;
};

@Injectable({ providedIn: 'root' })
export class OnboardingJourneyManagerService {
  private readonly onboarding = inject(OnboardingService);
  private readonly auth = inject(AuthService);
  private readonly jwt = inject(JwtService);
  private readonly permission = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly userExperienceLevel = inject(UserExperienceLevelService);

  /**
   * بعد إكمال المسار الإلزامي ننتقل برمجياً للوحة التحكم؛ لا يُعتبر ذلك «خروجاً يدوياً»
   * عن بداية سريعة (يمنع تعليق stage4 والمعالجات خطأً).
   */
  private suppressStage4ExitOnNextDashboardNavigation = false;

  private readonly state = signal<OnboardingJourneyState>({
    role: null,
    experienceLevel: 'intermediate',
    status: 'not_started',
    currentStep: 'done',
    postModalShown: false,
    lastError: null,
    hydrated: false,
    steps: FLOW_STEPS_OWNER_BEGINNER.map((step) => ({ step, completed: step === 'done' })),
  });
  private readonly pendingResumeWelcome = signal(false);

  readonly journeyState = computed(() => this.state());
  readonly isHydrated = computed(() => this.state().hydrated);
  readonly lastError = computed(() => this.state().lastError);
  readonly isOwnerBeginner = computed(
    () => this.state().role === 'businessOwner' && this.state().experienceLevel === 'beginner'
  );

  consumeResumeWelcomeNotice(): boolean {
    const pending = this.pendingResumeWelcome();
    if (!pending) {
      return false;
    }
    this.pendingResumeWelcome.set(false);
    return true;
  }
  /** إعداد إلزامي: صاحب المنشأة أو محاسب أو تقني (مستويات مبتدئ/متوسط/خبير حسب المسار) */
  readonly isMandatoryInProgress = computed(() => {
    const s = this.state();
    if (s.status !== 'in_progress') {
      return false;
    }
    if (s.role === 'businessOwner') {
      return (
        s.experienceLevel === 'beginner' ||
        s.experienceLevel === 'intermediate' ||
        s.experienceLevel === 'expert'
      );
    }
    if (s.role === 'accountant') {
      return (
        s.experienceLevel === 'beginner' ||
        s.experienceLevel === 'intermediate' ||
        s.experienceLevel === 'expert'
      );
    }
    if (s.role === 'technician') {
      return (
        s.experienceLevel === 'beginner' ||
        s.experienceLevel === 'intermediate' ||
        s.experienceLevel === 'expert'
      );
    }
    return false;
  });

  ensureHydrated(): Observable<boolean> {
    if (this.isHydrated()) {
      return of(true);
    }

    return this.hydrateFromBackend().pipe(
      catchError(() => {
        this.patchState({ hydrated: true });
        return of(true);
      })
    );
  }

  hydrateFromBackend(): Observable<boolean> {
    return this.auth.getUserProfile().pipe(
      map((response: unknown) => this.normalizeProfile(response)),
      tap((profile) => this.applyHydration(profile)),
      map(() => true),
      catchError(() => {
        const cached = this.readCache();
        if (cached) {
          const previousStatus = this.state().status;
          const restored = this.buildState({
            ...cached,
            hydrated: true,
            lastError: null,
          });
          this.state.set(restored);
          this.userExperienceLevel.setOwnerMandatoryFlowSuspended(restored.status === 'suspended');
          const shouldNotifyResume =
            restored.status === 'in_progress' &&
            restored.currentStep !== 'done' &&
            previousStatus !== 'in_progress';
          this.pendingResumeWelcome.set(shouldNotifyResume);
          return of(true);
        }

        this.patchState({ hydrated: true, lastError: null });
        this.pendingResumeWelcome.set(false);
        return of(true);
      })
    );
  }

  startOwnerBeginnerFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'businessOwner',
      experienceLevel: 'beginner',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('businessOwner', 'beginner'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startOwnerIntermediateFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'businessOwner',
      experienceLevel: 'intermediate',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('businessOwner', 'intermediate'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startOwnerExpertFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'businessOwner',
      experienceLevel: 'expert',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('businessOwner', 'expert'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startAccountantBeginnerFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'accountant',
      experienceLevel: 'beginner',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('accountant', 'beginner'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startAccountantIntermediateFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'accountant',
      experienceLevel: 'intermediate',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('accountant', 'intermediate'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startAccountantExpertFlow(): Observable<boolean> {
    const nextState = this.buildState({
      role: 'accountant',
      experienceLevel: 'expert',
      status: 'in_progress',
      currentStep: 'company',
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('accountant', 'expert'),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  startTechnicianBeginnerFlow(): Observable<boolean> {
    return this.startTechnicianFlow('beginner', 'users');
  }

  startTechnicianIntermediateFlow(): Observable<boolean> {
    return this.startTechnicianFlow('intermediate', 'users');
  }

  startTechnicianExpertFlow(): Observable<boolean> {
    return this.startTechnicianFlow('expert', 'roles');
  }

  private startTechnicianFlow(
    level: OnboardingExperienceLevel,
    firstStep: MandatoryOnboardingStep
  ): Observable<boolean> {
    const nextState = this.buildState({
      role: 'technician',
      experienceLevel: level,
      status: 'in_progress',
      currentStep: firstStep,
      postModalShown: false,
      hydrated: true,
      lastError: null,
      steps: this.stepsProgressForFlow('technician', level),
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    return this.persistJourney(nextState).pipe(
      tap(() => this.router.navigateByUrl(this.resolveRequiredRoute(nextState.currentStep))),
      map(() => true),
      catchError(() => {
        this.patchState({ lastError: 'تعذر بدء مسار الإعداد، يرجى المحاولة مرة أخرى.' });
        return of(false);
      })
    );
  }

  /**
   * خروج المستخدم عن مسار الإعداد الإلزامي (تنقل حر) — تعليق دون مودالات أو معالجات تلقائية.
   */
  suspendMandatoryJourneyForVoluntaryNavigation(): void {
    if (!this.isMandatoryInProgress()) {
      return;
    }
    const nextState = this.buildState({
      ...this.state(),
      status: 'suspended',
      lastError: null,
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(true);
    this.persistJourney(nextState).subscribe({ error: () => void 0 });
  }

  /**
   * استئناف المسار من صفحة إعدادات المعالج الذكي فقط.
   */
  /**
   * للـ Layout: تنقّل لوحة التحكم التالي بعد إكمال الإلزامي لا يُفعّل تعليق «بداية سريعة».
   */
  consumeSuppressStage4ExitOnNextDashboardNavigation(): boolean {
    if (!this.suppressStage4ExitOnNextDashboardNavigation) {
      return false;
    }
    this.suppressStage4ExitOnNextDashboardNavigation = false;
    return true;
  }

  private markDashboardNavigationAfterMandatoryFlowCompletion(): void {
    this.suppressStage4ExitOnNextDashboardNavigation = true;
  }

  resumeMandatoryJourneyFromSettings(): void {
    const s = this.state();
    if (s.status !== 'suspended') {
      return;
    }
    if (s.role === 'businessOwner') {
      if (
        s.experienceLevel !== 'beginner' &&
        s.experienceLevel !== 'intermediate' &&
        s.experienceLevel !== 'expert'
      ) {
        return;
      }
    } else if (s.role === 'accountant' || s.role === 'technician') {
      if (
        s.experienceLevel !== 'beginner' &&
        s.experienceLevel !== 'intermediate' &&
        s.experienceLevel !== 'expert'
      ) {
        return;
      }
    } else {
      return;
    }
    const nextState = this.buildState({
      ...s,
      status: 'in_progress',
      lastError: null,
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);
    // From Smart Wizard settings, always open onboarding landing first.
    // This keeps entry to the wizard independent from module-step permissions.
    void this.router.navigate(['/onboarding/start']);
    this.persistJourney(nextState).subscribe({ error: () => void 0 });
  }

  /** أثناء الإعداد الإلزامي لا نعرض مودال «بداية سريعة» من الـ Layout */
  isOwnerMandatorySetupInProgress(): boolean {
    return this.isMandatoryInProgress();
  }

  /**
   * بعد إكمال إعداد صاحب المنشأة (متوسط أو خبير): مودال «بداية سريعة» يُفتح من لوحة التحكم فقط
   * (وليس من الـ Layout عند أول تحميل). المبتدئ يستخدم مودال ما بعد الإعداد من الـ Dashboard.
   */
  shouldDeferStage4MiniExperienceToDashboard(): boolean {
    const j = this.state();
    if (j.status !== 'completed') {
      return false;
    }
    if (
      j.role === 'businessOwner' &&
      (j.experienceLevel === 'intermediate' || j.experienceLevel === 'expert')
    ) {
      return true;
    }
    return j.role === 'accountant' && (j.experienceLevel === 'intermediate' || j.experienceLevel === 'expert');
  }

  /**
   * لقطة تقدّم مسار الإعداد الإلزامي (مودال الاستئناف).
   */
  getJourneyProgress(): JourneyProgressSnapshot | null {
    type JourneyStep = Exclude<MandatoryOnboardingStep, 'done'>;
    const s = this.state();
    if (s.status !== 'in_progress' && s.status !== 'suspended') {
      return null;
    }
    const flow = this.rawFlowStepsUnfiltered(s.role, s.experienceLevel).filter(
      (step): step is JourneyStep => step !== 'done'
    );
    if (flow.length === 0) {
      return null;
    }

    const firstPendingFromSteps =
      (s.steps.find((entry): entry is { step: JourneyStep; completed: boolean } => entry.step !== 'done' && !entry.completed)
        ?.step ?? null);
    const current: JourneyStep =
      s.currentStep === 'done' ? firstPendingFromSteps ?? flow[0] : (s.currentStep as JourneyStep);

    const idx = flow.indexOf(current);
    if (idx < 0) {
      const fallbackStep = firstPendingFromSteps ?? flow[0];
      const fallbackIdx = flow.indexOf(fallbackStep);
      if (fallbackIdx < 0) {
        return null;
      }
      const totalSteps = flow.length;
      const completedSteps = fallbackIdx;
      const percentComplete =
        totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
      return {
        percentComplete,
        completedSteps,
        totalSteps,
        currentStep: fallbackStep,
      };
    }
    const totalSteps = flow.length;
    const completedSteps = idx;
    const percentComplete =
      totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
    return {
      percentComplete,
      completedSteps,
      totalSteps,
      currentStep: current,
    };
  }

  /** Canonical flow before permission filtering (used to resolve next step when current step was filtered out). */
  private rawFlowStepsUnfiltered(
    role: OnboardingRole | null,
    level: OnboardingExperienceLevel
  ): MandatoryOnboardingStep[] {
    if (role === 'technician' && level === 'expert') {
      return [...FLOW_STEPS_TECHNICIAN_EXPERT];
    }
    if (role === 'technician' && level === 'intermediate') {
      return [...FLOW_STEPS_TECHNICIAN_INTERMEDIATE];
    }
    if (role === 'technician' && level === 'beginner') {
      return [...FLOW_STEPS_TECHNICIAN_BEGINNER];
    }
    if (role === 'accountant' && level === 'expert') {
      return [...FLOW_STEPS_ACCOUNTANT_EXPERT];
    }
    if (role === 'accountant' && level === 'intermediate') {
      return [...FLOW_STEPS_ACCOUNTANT_INTERMEDIATE];
    }
    if (role === 'accountant' && level === 'beginner') {
      return [...FLOW_STEPS_ACCOUNTANT_BEGINNER];
    }
    if (role === 'businessOwner' && level === 'expert') {
      return [...FLOW_STEPS_OWNER_EXPERT];
    }
    if (role === 'businessOwner' && level === 'intermediate') {
      return [...FLOW_STEPS_OWNER_INTERMEDIATE];
    }
    return [...FLOW_STEPS_OWNER_BEGINNER];
  }

  /** Next step that exists in the permission-filtered flow after `completedStep` in the canonical order. */
  private nextFilteredStepAfter(completedStep: MandatoryOnboardingStep): MandatoryOnboardingStep | null {
    const raw = this.rawFlowStepsUnfiltered(this.state().role, this.state().experienceLevel);
    const filtered = this.activeFlowSteps();
    const pos = raw.indexOf(completedStep);
    if (pos < 0) {
      return null;
    }
    for (let i = pos + 1; i < raw.length; i++) {
      const candidate = raw[i];
      if (filtered.includes(candidate)) {
        return candidate;
      }
    }
    return null;
  }

  private flowStepsFor(
    role: OnboardingRole | null,
    level: OnboardingExperienceLevel
  ): MandatoryOnboardingStep[] {
    const finalize = (steps: MandatoryOnboardingStep[]): MandatoryOnboardingStep[] =>
      this.filterFlowStepsByPermission(steps);

    if (role === 'technician' && level === 'expert') {
      return finalize(FLOW_STEPS_TECHNICIAN_EXPERT);
    }
    if (role === 'technician' && level === 'intermediate') {
      return finalize(FLOW_STEPS_TECHNICIAN_INTERMEDIATE);
    }
    if (role === 'technician' && level === 'beginner') {
      return finalize(FLOW_STEPS_TECHNICIAN_BEGINNER);
    }
    if (role === 'accountant' && level === 'expert') {
      return finalize(FLOW_STEPS_ACCOUNTANT_EXPERT);
    }
    if (role === 'accountant' && level === 'intermediate') {
      return finalize(FLOW_STEPS_ACCOUNTANT_INTERMEDIATE);
    }
    if (role === 'accountant' && level === 'beginner') {
      return finalize(FLOW_STEPS_ACCOUNTANT_BEGINNER);
    }
    if (role === 'businessOwner' && level === 'expert') {
      return finalize(FLOW_STEPS_OWNER_EXPERT);
    }
    if (role === 'businessOwner' && level === 'intermediate') {
      return finalize(FLOW_STEPS_OWNER_INTERMEDIATE);
    }
    return finalize(FLOW_STEPS_OWNER_BEGINNER);
  }

  private filterFlowStepsByPermission(steps: MandatoryOnboardingStep[]): MandatoryOnboardingStep[] {
    const filtered = steps.filter((step) => this.isMandatoryStepAllowed(step));
    if (!filtered.includes('done')) {
      filtered.push('done');
    }
    return filtered.length > 0 ? filtered : ['done'];
  }

  private isMandatoryStepAllowed(step: MandatoryOnboardingStep): boolean {
    if (step === 'done') return true;
    const rule = MANDATORY_STEP_REQUIRED_PERMISSIONS[step];
    if (!rule || rule.keys.length === 0) return true;
    if (rule.mode === 'all') {
      return rule.keys.every((k) => this.permission.hasPermission(k));
    }
    return rule.keys.some((k) => this.permission.hasPermission(k));
  }

  private stepsProgressForFlow(
    role: OnboardingRole | null,
    level: OnboardingExperienceLevel
  ): MandatoryStepProgress[] {
    return this.flowStepsFor(role, level).map((step) => ({
      step,
      completed: step === 'done',
    }));
  }

  private activeFlowSteps(): MandatoryOnboardingStep[] {
    const j = this.state();
    return this.flowStepsFor(j.role, j.experienceLevel);
  }

  resolveRequiredRoute(step: MandatoryOnboardingStep = this.state().currentStep): string {
    const mapByStep: Record<MandatoryOnboardingStep, string> = {
      company: '/settings/currentCompany/edit/current',
      currency: '/settings/currencies',
      fiscalYear: '/fiscal-years/list',
      glAccounts: '/accounting/accounts/list-accounts',
      intermediateAccount: '/settings/intermediate-accounts',
      receiptVoucher: '/accounting/receipt-vouchers/list-receipt-vouchers',
      paymentVoucher: '/accounting/payment-vouchers/list-payment-vouchers',
      seedData: this.resolveSeedDataRoute(),
      store: '/stock/stores/list-store',
      productGroup: '/stock/product-categories/list-product-category',
      barcode: '/stock/products-barcode',
      users: '/users/list/create-user/create/new',
      roles: '/roles/create-update-role/create/new',
      branch: '/settings/branch/branches-list',
      dailyJournal: '/accounting/journals/list-journals',
      salesInvoice: '/sales/sales-invoices/create-invoice/create/new',
      purchaseInvoice: '/purchases/purchases-invoices/create-invoice/create/new',
      traineeCustomer: '/sales/customers/customers-list',
      traineeVendor: '/purchases/vendors/vendors-list',
      traineeProduct: '/stock/products/list-products',
      shift: '/shifts/list-shifts',
      pos: '/pos-invoices/create-invoice/create/new',
      manualInventory: '/manual-inventory/list-manual-inventories',
      openingStock: '/opening-stock/list-opening-stocks',
      zatcaUnit: '/zatca/zatcaUnits',
      electronicInvoicing: '/zatca/zatca-invoices-list',
      done: '/dashboard',
    };

    return mapByStep[step];
  }

  isCurrentRouteAllowed(url: string): boolean {
    if (!this.isMandatoryInProgress()) {
      return true;
    }

    const currentStepRoutes = this.resolveAllowedRoutesForCurrentStep();
    const normalized = url.split('?')[0].toLowerCase();
    return currentStepRoutes.some((route) => normalized.startsWith(route.toLowerCase()));
  }

  /**
   * مسار الإعداد لصاحب المنشأة المبتدئ: عملاء → أصناف → مستودعات → فئات…
   * (إلزامي، أو خطوة رحلة صريحة، أو Stage1 / بروفايل / حقول الرحلة).
   */
  isOwnerBeginnerSeedContext(): boolean {
    if (this.state().status === 'suspended') {
      return false;
    }
    if (this.isMandatoryInProgress()) {
      const j = this.state();
      if (j.role === 'businessOwner' && j.experienceLevel === 'expert') {
        return false;
      }
      if (j.role === 'accountant' || j.role === 'technician') {
        return false;
      }
      return true;
    }

    const j = this.state();
    if (
      j.currentStep === 'seedData' ||
      j.currentStep === 'store' ||
      j.currentStep === 'productGroup' ||
      j.currentStep === 'barcode'
    ) {
      return true;
    }

    const ownerBeginnerFromStage1 =
      this.userExperienceLevel.effectiveRole() === 'owner' &&
      this.userExperienceLevel.effectiveLevel() === 'beginner';

    const profileOwnerBeginner =
      this.onboarding.role() === 'businessOwner' &&
      this.onboarding.experienceLevel() === 'beginner';

    if (ownerBeginnerFromStage1 || profileOwnerBeginner || this.isOwnerBeginner()) {
      return true;
    }

    if (j.status === 'completed') {
      return false;
    }

    return false;
  }

  /** زر «تم» على إنشاء عميل: الانتقال لقائمة الأصناف ضمن نفس سياق `isOwnerBeginnerSeedContext`. */
  isSeedDataCustomerSubmitShouldOpenProducts(currentUrl: string): boolean {
    const path = currentUrl.split('?')[0].toLowerCase();
    if (!path.includes('sales/customers/create-customer/create')) {
      return false;
    }
    return this.isOwnerBeginnerSeedContext();
  }

  syncCurrentStepFromWizardProgress(): void {
    if (!this.isMandatoryInProgress()) {
      return;
    }

    const currentStep = this.state().currentStep;
    const shouldAdvance = this.isStepMarkedAsDone(currentStep);
    if (!shouldAdvance) {
      return;
    }

    this.completeStep(currentStep);
  }

  /**
   * From in-page coach marks ("Skip tutorial"): while mandatory onboarding (or owner-beginner seed path)
   * is active, skip completes the current journey step and navigates to the next; otherwise only
   * {@link localDismiss} runs (per-screen wizard dismissed only).
   */
  handleCoachmarkSkipDuringOnboarding(localDismiss: () => void): void {
    const step = this.state().currentStep;
    if (step === 'done') {
      localDismiss();
      return;
    }
    const s = this.state();
    const suspendedResumable =
      s.status === 'suspended' &&
      (s.role === 'businessOwner' || s.role === 'accountant' || s.role === 'technician');
    const shouldAdvanceGlobally =
      this.isMandatoryInProgress() ||
      this.isOwnerBeginnerSeedContext() ||
      suspendedResumable;
    if (!shouldAdvanceGlobally) {
      localDismiss();
      return;
    }
    localDismiss();
    this.completeStep(step);
  }

  completeStep(step: MandatoryOnboardingStep): void {
    if (step !== this.state().currentStep) {
      return;
    }

    if (this.state().status === 'suspended') {
      this.patchState({ status: 'in_progress', lastError: null });
      this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);
    }

    if (!this.isMandatoryInProgress() && !this.isOwnerBeginnerSeedContext()) {
      return;
    }

    const flow = this.activeFlowSteps();
    const currentIndex = flow.indexOf(step);
    let nextStep: MandatoryOnboardingStep;
    if (currentIndex >= 0) {
      nextStep = flow[Math.min(currentIndex + 1, flow.length - 1)];
    } else {
      const resolved = this.nextFilteredStepAfter(step);
      if (!resolved) {
        return;
      }
      nextStep = resolved;
    }
    const completedAt = Date.now();

    const nextState = this.buildState({
      ...this.state(),
      currentStep: nextStep,
      steps: this.state().steps.map((x) =>
        x.step === step ? { ...x, completed: true, completedAt } : x
      ),
      lastError: null,
    });

    if (nextStep === 'done') {
      nextState.status = 'completed';
    }

    this.state.set(nextState);
    this.writeCache(nextState);
    if (nextStep === 'done') {
      this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);
    }

    this.persistJourney(nextState).subscribe({
      next: (persisted) => {
        if (!persisted) {
          this.patchState({
            lastError: 'تعذر مزامنة حالة الإعداد مع الخادم، سيتم المتابعة محلياً.',
          });
        }
        if (nextStep === 'done') {
          this.onboarding.markCompleted().subscribe({
            next: () => {
              this.markDashboardNavigationAfterMandatoryFlowCompletion();
              void this.router.navigateByUrl('/dashboard');
            },
            error: () => {
              this.markDashboardNavigationAfterMandatoryFlowCompletion();
              void this.router.navigateByUrl('/dashboard');
            },
          });
        } else {
          this.router.navigateByUrl(this.resolveRequiredRoute(nextStep));
        }
      },
    });
  }

  skipBarcodeStep(): void {
    if (this.state().currentStep !== 'barcode') {
      return;
    }

    const nextState = this.buildState({
      ...this.state(),
      steps: this.state().steps.map((x) =>
        x.step === 'barcode' ? { ...x, skipped: true, completed: true, completedAt: Date.now() } : x
      ),
      currentStep: 'done',
      status: 'completed',
      lastError: null,
    });

    this.state.set(nextState);
    this.writeCache(nextState);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(false);

    this.persistJourney(nextState).subscribe({
      next: (persisted) => {
        if (!persisted) {
          this.patchState({
            lastError: 'تعذر مزامنة حالة الإعداد مع الخادم، سيتم المتابعة محلياً.',
          });
        }
        this.onboarding.markCompleted().subscribe({
          next: () => {
            this.markDashboardNavigationAfterMandatoryFlowCompletion();
            void this.router.navigateByUrl('/dashboard');
          },
          error: () => {
            this.markDashboardNavigationAfterMandatoryFlowCompletion();
            void this.router.navigateByUrl('/dashboard');
          },
        });
      },
    });
  }

  retryCurrentStep(): void {
    const route = this.resolveRequiredRoute();
    this.patchState({ lastError: null });
    this.router.navigateByUrl(route);
  }

  shouldShowPostOnboardingModal(): boolean {
    const j = this.state();
    return (
      this.isOwnerBeginner() &&
      j.status === 'completed' &&
      j.currentStep === 'done' &&
      !j.postModalShown
    );
  }

  markPostOnboardingModalShown(): void {
    const nextState = this.buildState({
      ...this.state(),
      postModalShown: true,
      lastError: null,
    });
    this.state.set(nextState);
    this.writeCache(nextState);
    this.persistJourney(nextState).subscribe({ error: () => void 0 });
  }

  private applyHydration(profile: RemoteProfilePayload): void {
    const previousStatus = this.state().status;
    const cached = this.readCache();
    const remoteRole = profile.onboardingRole ?? this.onboarding.role();
    // OnboardingService defaults experienceLevel to 'intermediate'; do not let that overwrite
    // an in-progress owner-beginner journey that only exists in local cache.
    const apiLevel = profile.onboardingExperienceLevel;
    const svcLevel = this.onboarding.experienceLevel();
    const experienceLevel =
      apiLevel ??
      (cached?.status === 'in_progress' ? cached?.experienceLevel : undefined) ??
      svcLevel ??
      cached?.experienceLevel ??
      'intermediate';

    const remoteCompleted = profile.isOnboardingCompleted ?? this.onboarding.isCompleted();
    const remoteStatusRaw = profile.onboardingJourney?.status ?? profile.onboardingStatus;
    const apiStatus = this.asStatus(remoteStatusRaw);
    const remoteStep = profile.onboardingJourney?.currentStep ?? profile.onboardingCurrentStep;
    const remotePostModalShown =
      profile.onboardingJourney?.postModalShown ?? profile.postOnboardingModalShown ?? false;

    const roleForFlow = remoteRole ?? cached?.role ?? null;

    /**
     * أولوية الكاش للمسار الإلزامي المحلي:
     * الخادم غالباً يُرجع isOnboardingCompleted=true مبكراً فيُصنَّف المستخدم «مكتملاً» قبل إنهاء
     * معالجات الشركة/العملة/… فيُفتح مودال «ابدأ بإنجاز مهامك الأولى» للمبتدئ خطأً.
     */
    let status: MandatoryOnboardingStatus;
    if (cached?.status === 'suspended') {
      status = 'suspended';
    } else if (cached?.status === 'in_progress') {
      status = 'in_progress';
    } else if (remoteCompleted) {
      status = 'completed';
    } else {
      status = apiStatus ?? cached?.status ?? 'not_started';
    }

    let currentStep: MandatoryOnboardingStep =
      status === 'completed'
        ? 'done'
        : remoteStep ?? cached?.currentStep ?? 'company';
    if (roleForFlow === 'businessOwner' && experienceLevel === 'intermediate' && currentStep === 'seedData') {
      currentStep = 'store';
    }

    const effectiveFlow =
      status === 'in_progress' || status === 'suspended'
        ? this.rawFlowStepsUnfiltered(roleForFlow, experienceLevel)
        : this.flowStepsFor(roleForFlow, experienceLevel);
    if (status !== 'completed' && !effectiveFlow.includes(currentStep)) {
      currentStep = effectiveFlow[0] ?? 'company';
    }

    let steps = cached?.steps;
    const stepsMatchFlow =
      !!steps &&
      steps.length === effectiveFlow.length &&
      effectiveFlow.every((s, i) => steps![i]?.step === s);
    if (!stepsMatchFlow) {
      steps = effectiveFlow.map((step) => ({
        step,
        completed: step === 'done',
      }));
    }

    const postModalShown =
      status === 'in_progress' || status === 'suspended'
        ? cached?.postModalShown ?? false
        : remotePostModalShown || cached?.postModalShown || false;

    const state = this.buildState({
      role: remoteRole ?? cached?.role ?? null,
      experienceLevel,
      status,
      currentStep,
      postModalShown,
      hydrated: true,
      lastError: null,
      steps,
    });

    this.state.set(state);
    this.writeCache(state);
    this.userExperienceLevel.setOwnerMandatoryFlowSuspended(state.status === 'suspended');
    const shouldNotifyResume =
      state.status === 'in_progress' &&
      state.currentStep !== 'done' &&
      previousStatus !== 'in_progress';
    this.pendingResumeWelcome.set(shouldNotifyResume);
  }

  private normalizeProfile(response: unknown): RemoteProfilePayload {
    const raw = (response ?? {}) as Record<string, unknown>;
    const data = (raw['data'] ?? raw) as Record<string, unknown>;
    const onboardingJourney = (data['onboardingJourney'] ?? {}) as Record<string, unknown>;

    return {
      isOnboardingCompleted: this.asBoolean(data['isOnboardingCompleted']),
      onboardingRole: this.asRole(data['onboardingRole']),
      onboardingExperienceLevel: this.asLevel(data['onboardingExperienceLevel']),
      onboardingCurrentStep: this.asStep(data['onboardingCurrentStep']),
      onboardingStatus: this.asStatus(data['onboardingStatus']),
      postOnboardingModalShown: this.asBoolean(data['postOnboardingModalShown']),
      onboardingJourney: {
        status: this.asStatus(onboardingJourney['status']),
        currentStep: this.asStep(onboardingJourney['currentStep']),
        postModalShown: this.asBoolean(onboardingJourney['postModalShown']),
      },
    };
  }

  private isStepMarkedAsDone(step: MandatoryOnboardingStep): boolean {
    const stepKeys: Record<MandatoryOnboardingStep, string | null> = {
      company: 'company_wizard_progress',
      currency: 'currency_wizard_progress',
      fiscalYear: 'financial_year_wizard_progress',
      glAccounts: 'chart_accounts_wizard_progress',
      intermediateAccount: 'intermediate_account_wizard_progress',
      receiptVoucher: 'receipt_voucher_wizard_progress',
      paymentVoucher: 'payment_voucher_wizard_progress',
      seedData: null,
      store: 'store_wizard_progress',
      productGroup: 'product_category_wizard_progress',
      barcode: 'products_barcodes_wizard_progress',
      users: 'users_wizard_progress',
      roles: 'roles_wizard_progress',
      branch: 'branch_wizard_progress',
      dailyJournal: 'daily_journal_wizard_progress',
      salesInvoice: 'sales_invoice_wizard_progress',
      purchaseInvoice: 'purchase_invoice_wizard_progress',
      traineeCustomer: 'contact_wizard_progress_CUSTOMERS',
      traineeVendor: 'contact_wizard_progress_VENDORS',
      traineeProduct: 'product_wizard_progress',
      shift: 'shift_wizard_progress',
      pos: 'pos_wizard_progress',
      manualInventory: 'manual_inventory_wizard_progress',
      openingStock: 'opening_stock_wizard_progress',
      zatcaUnit: 'zatca_unit_wizard_progress',
      electronicInvoicing: 'electronic_invoice_wizard_progress',
      done: null,
    };

    if (step === 'seedData') {
      return this.isProgressCompleted('product_wizard_progress');
    }

    const key = stepKeys[step];
    if (!key) {
      return true;
    }

    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return false;
      }
      const progress = JSON.parse(raw) as { completedAt?: number; skipped?: boolean };
      if (step === 'barcode') {
        return (progress.completedAt ?? 0) > 0 || progress.skipped === true;
      }
      if (step === 'zatcaUnit' || step === 'electronicInvoicing') {
        return (progress.completedAt ?? 0) > 0 || progress.skipped === true;
      }
      return (progress.completedAt ?? 0) > 0;
    } catch {
      return false;
    }
  }

  private persistJourney(state: OnboardingJourneyState): Observable<boolean> {
    if (!ENABLE_BACKEND_SYNC) {
      return of(true);
    }
    return of(true);
  }

  private resolveAllowedRoutesForCurrentStep(): string[] {
    const step = this.state().currentStep;
    if (step === 'currency') {
      // List: .../currencies — Create wizard: .../currency/create/new (different path segment).
      return ['/settings/currencies', '/settings/currency'];
    }
    if (step === 'store') {
      // القائمة + معالج إنشاء/تعديل المستودع (دليل «أضف مستودع» يفتح create/new).
      return ['/stock/stores/list-store', '/stock/stores/create-update-store'];
    }
    if (step === 'productGroup') {
      return [
        '/stock/product-categories/list-product-category',
        '/stock/product-categories/create-update-product-category',
      ];
    }
    if (step === 'fiscalYear') {
      return ['/fiscal-years/list', '/fiscal-years/financial-year'];
    }
    if (step === 'glAccounts') {
      return ['/accounting/accounts/list-accounts', '/accounting/accounts/create-update-account'];
    }
    if (step === 'intermediateAccount') {
      return ['/settings/intermediate-accounts'];
    }
    if (step === 'users') {
      return ['/users/list/users-list', '/users/list/create-user'];
    }
    if (step === 'roles') {
      return ['/roles/list-roles', '/roles/create-update-role'];
    }
    if (step === 'branch') {
      return ['/settings/branch/branches-list', '/settings/branch/branch'];
    }
    if (step === 'receiptVoucher') {
      return [
        '/accounting/receipt-vouchers/list-receipt-vouchers',
        '/accounting/receipt-vouchers/create-or-update-receipt-voucher',
      ];
    }
    if (step === 'paymentVoucher') {
      return [
        '/accounting/payment-vouchers/list-payment-vouchers',
        '/accounting/payment-vouchers/create-or-update-payment-voucher',
      ];
    }
    if (step === 'dailyJournal') {
      return ['/accounting/journals/list-journals', '/accounting/journals/create-or-update-journal'];
    }
    if (step === 'salesInvoice') {
      return ['/sales/sales-invoices/invoice', '/sales/sales-invoices/create-invoice'];
    }
    if (step === 'purchaseInvoice') {
      return ['/purchases/purchases-invoices/invoice', '/purchases/purchases-invoices/create-invoice'];
    }
    if (step === 'traineeCustomer') {
      return ['/sales/customers/customers-list', '/sales/customers/create-customer'];
    }
    if (step === 'traineeVendor') {
      return ['/purchases/vendors/vendors-list', '/purchases/vendors/create-vendor'];
    }
    if (step === 'traineeProduct') {
      return ['/stock/products/list-products', '/stock/products/create-or-update-product'];
    }
    if (step === 'shift') {
      return ['/shifts/list-shifts', '/shifts/create-update-shift'];
    }
    if (step === 'pos') {
      return ['/pos-invoices/create-invoice', '/pos-invoices/list-invoices'];
    }
    if (step === 'manualInventory') {
      return ['/manual-inventory/list-manual-inventories', '/manual-inventory/create-manaul-inventory'];
    }
    if (step === 'openingStock') {
      return ['/opening-stock/list-opening-stocks', '/opening-stock/create-opening-stock'];
    }
    if (step === 'zatcaUnit') {
      return ['/zatca/zatcaUnits', '/zatca/zatcaUnit'];
    }
    if (step === 'electronicInvoicing') {
      return ['/zatca/zatca-invoices-list', '/zatca/view-zatca-invoice'];
    }
    if (step !== 'seedData') {
      return [this.resolveRequiredRoute(step)];
    }

    return [
      '/sales/customers/customers-list',
      '/sales/customers/create-customer/create',
      '/stock/products/list-products',
      '/stock/products/create-or-update-product/create/new',
    ];
  }

  private resolveSeedDataRoute(): string {
    if (this.isProgressCompleted('product_wizard_progress')) {
      return '/stock/products/list-products';
    }
    if (this.isCustomerContactWizardLegComplete()) {
      return '/stock/products/list-products';
    }
    return '/sales/customers/customers-list';
  }

  private isCustomerContactWizardLegComplete(): boolean {
    try {
      const raw = localStorage.getItem(CONTACT_WIZARD_PROGRESS_CUSTOMERS);
      if (!raw) {
        return false;
      }
      const p = JSON.parse(raw) as { completedAt?: number; skipped?: boolean };
      return (p.completedAt ?? 0) > 0 || p.skipped === true;
    } catch {
      return false;
    }
  }

  private isProgressCompleted(key: string): boolean {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return false;
      }
      const parsed = JSON.parse(raw) as { completedAt?: number; skipped?: boolean };
      return (parsed.completedAt ?? 0) > 0 || parsed.skipped === true;
    } catch {
      return false;
    }
  }

  private buildState(input: Partial<OnboardingJourneyState>): OnboardingJourneyState {
    const base = this.state();
    const role = input.role ?? base.role ?? null;
    const experienceLevel = input.experienceLevel ?? base.experienceLevel ?? 'intermediate';
    const merged: OnboardingJourneyState = {
      role,
      experienceLevel,
      status: input.status ?? base.status ?? 'not_started',
      currentStep: input.currentStep ?? base.currentStep ?? 'done',
      postModalShown: input.postModalShown ?? base.postModalShown ?? false,
      lastError: input.lastError ?? base.lastError ?? null,
      hydrated: input.hydrated ?? base.hydrated ?? false,
      steps:
        input.steps ??
        base.steps ??
        this.stepsProgressForFlow(role, experienceLevel),
    };

    return merged;
  }

  private patchState(patch: Partial<OnboardingJourneyState>): void {
    const next = this.buildState({
      ...this.state(),
      ...patch,
    });
    this.state.set(next);
    this.writeCache(next);
  }

  private getCacheKey(): string | null {
    const token = this.auth.getAccessToken();
    if (!token) {
      return null;
    }
    const userId = this.jwt.getUserId(token);
    if (!userId) {
      return null;
    }
    return `mandatory_onboarding_journey_${userId}`;
  }

  private readCache(): OnboardingJourneyState | null {
    try {
      const key = this.getCacheKey();
      if (!key) {
        return null;
      }
      const raw = localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      return this.buildState(JSON.parse(raw) as OnboardingJourneyState);
    } catch {
      return null;
    }
  }

  private writeCache(state: OnboardingJourneyState): void {
    try {
      const key = this.getCacheKey();
      if (!key) {
        return;
      }
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // ignore cache failures
    }
  }

  /**
   * بعد مسح مفتاح الرحلة من localStorage (مثل إعادة تعيين تقدم المعالجات من الإعدادات).
   */
  syncJourneyStateAfterWizardStorageReset(): void {
    const cached = this.readCache();
    if (cached) {
      this.state.set({ ...cached, hydrated: true });
      this.userExperienceLevel.setOwnerMandatoryFlowSuspended(cached.status === 'suspended');
      return;
    }
    this.state.set({
      role: null,
      experienceLevel: 'intermediate',
      status: 'not_started',
      currentStep: 'done',
      postModalShown: false,
      lastError: null,
      hydrated: true,
      steps: FLOW_STEPS_OWNER_BEGINNER.map((step) => ({ step, completed: step === 'done' })),
    });
  }

  private asBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value;
    return undefined;
  }

  private asRole(value: unknown): OnboardingRole | undefined {
    if (
      value === 'businessOwner' ||
      value === 'accountant' ||
      value === 'technician' ||
      value === 'trainee'
    ) {
      return value;
    }
    return undefined;
  }

  private asLevel(value: unknown): OnboardingExperienceLevel | undefined {
    if (value === 'beginner' || value === 'intermediate' || value === 'expert') {
      return value;
    }
    return undefined;
  }

  private asStep(value: unknown): MandatoryOnboardingStep | undefined {
    if (
      value === 'company' ||
      value === 'currency' ||
      value === 'fiscalYear' ||
      value === 'glAccounts' ||
      value === 'intermediateAccount' ||
      value === 'receiptVoucher' ||
      value === 'paymentVoucher' ||
      value === 'seedData' ||
      value === 'store' ||
      value === 'productGroup' ||
      value === 'barcode' ||
      value === 'users' ||
      value === 'roles' ||
      value === 'branch' ||
      value === 'dailyJournal' ||
      value === 'salesInvoice' ||
      value === 'purchaseInvoice' ||
      value === 'traineeCustomer' ||
      value === 'traineeVendor' ||
      value === 'traineeProduct' ||
      value === 'shift' ||
      value === 'pos' ||
      value === 'manualInventory' ||
      value === 'openingStock' ||
      value === 'zatcaUnit' ||
      value === 'electronicInvoicing' ||
      value === 'done'
    ) {
      return value;
    }
    return undefined;
  }

  private asStatus(value: unknown): MandatoryOnboardingStatus | undefined {
    if (
      value === 'not_started' ||
      value === 'in_progress' ||
      value === 'suspended' ||
      value === 'completed'
    ) {
      return value;
    }
    return undefined;
  }
}
